/**
 * Fase 2 (W39): "Nova versão disponível". Quem está com a tela aberta continuava na versão antiga até recarregar, sem
 * saber. O web consulta a versão no ar (`GET /health`, campo `versao` = `APP_SHA` da API) a cada 5 minutos e quando a aba
 * volta a ficar visível, e compara com a versão embutida no próprio build (`__VERSAO_WEB__`, o mesmo SHA, posto pelo
 * Vite). Diferente em duas leituras seguidas (30 s entre elas: no deploy, as réplicas reiniciam uma de cada vez e o
 * balanceador ainda pode responder pela antiga), o aviso aparece e fica até a pessoa atualizar ou fechar.
 *
 * A carga de um pedaço do app (chunk) que falha depois de um deploy (os arquivos da versão anterior saem do ar) mostra o
 * mesmo aviso em vez de quebrar a tela (`ehFalhaDeChunk`, o evento `vite:preloadError` e o limite de erro).
 */

declare const __VERSAO_WEB__: string | undefined;

/** Versão deste build (o SHA do deploy); `dev` fora do build de produção ou nos testes. */
export const VERSAO_WEB: string = typeof __VERSAO_WEB__ === 'string' && __VERSAO_WEB__ ? __VERSAO_WEB__ : 'dev';

export const INTERVALO_VERSAO_MS = 5 * 60_000;
export const CONFIRMACAO_VERSAO_MS = 30_000;
/** Intervalo mínimo entre duas consultas pelo foco (alternar abas várias vezes não vira rajada no /health). */
export const FOCO_MINIMO_MS = 15_000;

/** Versão comparável: não vazia, sem espaço, e não a de desenvolvimento. */
export function versaoValida(v: unknown): v is string {
  return typeof v === 'string' && /^[\w.-]{4,80}$/.test(v) && v !== 'dev';
}

/**
 * As duas versões são de builds diferentes. Uma inválida (dev, vazia) nunca conta como diferente; uma que é prefixo da
 * outra (SHA curto e longo) é a mesma.
 */
export function versoesDiferentes(build: unknown, noAr: unknown): boolean {
  if (!versaoValida(build) || !versaoValida(noAr)) return false;
  const a = build.toLowerCase();
  const b = noAr.toLowerCase();
  return !(a.startsWith(b) || b.startsWith(a));
}

export interface RelogioVigia {
  agora(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(id: unknown): void;
}

const relogioPadrao: RelogioVigia = {
  agora: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) => clearInterval(id as ReturnType<typeof setInterval>),
};

export interface OpcoesVigiaVersao {
  versaoBuild: string;
  /** Versão no ar; null quando não deu para saber (rede, servidor fora): nada muda. */
  buscar(): Promise<string | null>;
  aoMudar(versao: string): void;
  intervaloMs?: number;
  confirmacaoMs?: number;
  focoMinimoMs?: number;
  relogio?: RelogioVigia;
}

export interface VigiaVersao {
  /** Consulta agora (com a confirmação de 30 s quando a versão parece diferente). */
  verificar(): Promise<void>;
  /** Aba voltou ao foco: consulta, se a última foi há mais de 15 s. */
  aoFocar(): void;
  iniciar(): void;
  parar(): void;
  readonly avisado: string | null;
}

export function criarVigiaVersao(o: OpcoesVigiaVersao): VigiaVersao {
  const relogio = o.relogio ?? relogioPadrao;
  const intervalo = o.intervaloMs ?? INTERVALO_VERSAO_MS;
  const confirmacao = o.confirmacaoMs ?? CONFIRMACAO_VERSAO_MS;
  const focoMinimo = o.focoMinimoMs ?? FOCO_MINIMO_MS;
  let avisado: string | null = null;
  let suspeita = false;
  let consultando = false;
  let ultima = -Infinity;
  let timerIntervalo: unknown = null;
  let timerConfirmacao: unknown = null;
  let ativo = false;

  async function verificar() {
    if (avisado || consultando || !versaoValida(o.versaoBuild)) return;
    consultando = true;
    ultima = relogio.agora();
    let noAr: string | null = null;
    try {
      noAr = await o.buscar();
    } catch {
      noAr = null;
    } finally {
      consultando = false;
    }
    if (noAr === null) return;
    if (!versoesDiferentes(o.versaoBuild, noAr)) {
      suspeita = false;
      return;
    }
    if (suspeita) {
      suspeita = false;
      avisado = noAr;
      o.aoMudar(noAr);
      return;
    }
    // Primeira leitura diferente: confirma daqui a pouco (a réplica que respondeu pode ser a que ainda vai reiniciar).
    suspeita = true;
    if (timerConfirmacao !== null) relogio.clearTimeout(timerConfirmacao);
    timerConfirmacao = relogio.setTimeout(() => {
      timerConfirmacao = null;
      if (ativo) void verificar();
    }, confirmacao);
  }

  return {
    verificar,
    aoFocar() {
      if (!ativo || relogio.agora() - ultima < focoMinimo) return;
      void verificar();
    },
    iniciar() {
      if (ativo || !versaoValida(o.versaoBuild)) return;
      ativo = true;
      timerIntervalo = relogio.setInterval(() => void verificar(), intervalo);
    },
    parar() {
      ativo = false;
      if (timerIntervalo !== null) relogio.clearInterval(timerIntervalo);
      if (timerConfirmacao !== null) relogio.clearTimeout(timerConfirmacao);
      timerIntervalo = timerConfirmacao = null;
    },
    get avisado() {
      return avisado;
    },
  };
}

/** Versão no ar pelo `/health` (sem cache; o corpo traz `versao`). */
export async function buscarVersaoNoAr(f: typeof fetch = fetch): Promise<string | null> {
  const r = await f('/health', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!r.ok) return null;
  const corpo = (await r.json().catch(() => null)) as { versao?: unknown } | null;
  return versaoValida(corpo?.versao) ? corpo.versao : null;
}

/** Erro de carga de um pedaço do app (import dinâmico ou CSS) que sumiu com o deploy. */
export function ehFalhaDeChunk(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name} ${e.message}` : typeof e === 'string' ? e : '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed/i.test(msg);
}

// ------------------------------------------------------------- estado do aviso (um por aba, lido pelo componente)
export type MotivoNovaVersao = 'versao' | 'chunk';
interface EstadoAviso {
  visivel: boolean;
  motivo: MotivoNovaVersao | null;
}
let estado: EstadoAviso = { visivel: false, motivo: null };
const ouvintes = new Set<() => void>();

export function estadoNovaVersao(): EstadoAviso {
  return estado;
}

export function assinarNovaVersao(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

/** Mostra o aviso (a versão no ar mudou, ou um pedaço do app não carregou). */
export function sinalizarNovaVersao(motivo: MotivoNovaVersao) {
  if (estado.visivel && estado.motivo === motivo) return;
  estado = { visivel: true, motivo };
  ouvintes.forEach((f) => f());
}

/** A pessoa fechou o aviso: some até o próximo sinal (chunk que falhar, por exemplo). */
export function dispensarNovaVersao() {
  if (!estado.visivel) return;
  estado = { visivel: false, motivo: estado.motivo };
  ouvintes.forEach((f) => f());
}

/** Só para os testes. */
export function zerarNovaVersao() {
  estado = { visivel: false, motivo: null };
  ouvintes.forEach((f) => f());
}

/** Recarrega a página (o botão "Atualizar"). */
export function recarregarPagina() {
  window.location.reload();
}
